import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTargetsThunk,
  fetchMyTargetsThunk,
  fetchTargetThunk,
  fetchTargetProgressThunk,
  createTargetThunk,
  updateTargetThunk,
  deleteTargetThunk,
  fetchTargetSummaryThunk,
} from './employeeTargetThunks';

const initialState = {
  // All targets (admin view)
  targets: [],
  loading: false,
  error: null,
  next: null,
  count: 0,
  
  // Current employee's targets
  myTargets: {
    list: [],
    loading: false,
    error: null,
  },
  
  // Detailed view of a specific target
  currentTarget: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Progress details for a target
  targetProgress: {
    target: null,
    reports: [],
    reportsCount: 0,
    remaining: 0,
    loading: false,
    error: null,
  },
  
  // Summary statistics
  summary: {
    totalActiveTargets: 0,
    notStarted: 0,
    inProgress: 0,
    completed: 0,
    employeesWithoutTargets: 0,
    loading: false,
    error: null,
  },
};

const employeeTargetSlice = createSlice({
  name: 'employeeTargets',
  initialState,
  reducers: {
    resetTargets(state) {
      state.targets = [];
      state.next = null;
      state.count = 0;
      state.error = null;
    },
    resetMyTargets(state) {
      state.myTargets = { ...initialState.myTargets };
    },
    resetCurrentTarget(state) {
      state.currentTarget = { ...initialState.currentTarget };
    },
    resetTargetProgress(state) {
      state.targetProgress = { ...initialState.targetProgress };
    },
    resetTargetSummary(state) {
      state.summary = { ...initialState.summary };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all targets (admin view)
      .addCase(fetchTargetsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTargetsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { results, next, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        offset > 0 ? state.targets.push(...results) : (state.targets = results);
        state.next = next;
        state.count = count;
      })
      .addCase(fetchTargetsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch my targets (employee view)
      .addCase(fetchMyTargetsThunk.pending, (state) => {
        state.myTargets.loading = true;
        state.myTargets.error = null;
      })
      .addCase(fetchMyTargetsThunk.fulfilled, (state, action) => {
        state.myTargets.loading = false;
        state.myTargets.list = action.payload;
      })
      .addCase(fetchMyTargetsThunk.rejected, (state, action) => {
        state.myTargets.loading = false;
        state.myTargets.error = action.payload;
      })
      
      // Fetch single target
      .addCase(fetchTargetThunk.pending, (state) => {
        state.currentTarget.loading = true;
        state.currentTarget.error = null;
      })
      .addCase(fetchTargetThunk.fulfilled, (state, action) => {
        state.currentTarget.loading = false;
        state.currentTarget.data = action.payload;
      })
      .addCase(fetchTargetThunk.rejected, (state, action) => {
        state.currentTarget.loading = false;
        state.currentTarget.error = action.payload;
      })
      
      // Fetch target progress
      .addCase(fetchTargetProgressThunk.pending, (state) => {
        state.targetProgress.loading = true;
        state.targetProgress.error = null;
      })
      .addCase(fetchTargetProgressThunk.fulfilled, (state, action) => {
        state.targetProgress.loading = false;
        state.targetProgress.target = action.payload.target;
        state.targetProgress.reports = action.payload.reports;
        state.targetProgress.reportsCount = action.payload.reports_count;
        state.targetProgress.remaining = action.payload.remaining;
      })
      .addCase(fetchTargetProgressThunk.rejected, (state, action) => {
        state.targetProgress.loading = false;
        state.targetProgress.error = action.payload;
      })
      
      // Create target
      .addCase(createTargetThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTargetThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.targets.unshift(action.payload);
        state.count += 1;
      })
      .addCase(createTargetThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update target
      .addCase(updateTargetThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTargetThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTarget = action.payload;
        
        // Update in targets list if exists
        const idx = state.targets.findIndex(t => t.id === updatedTarget.id);
        if (idx !== -1) state.targets[idx] = updatedTarget;
        
        // Update in myTargets list if exists
        const myIdx = state.myTargets.list.findIndex(t => t.id === updatedTarget.id);
        if (myIdx !== -1) state.myTargets.list[myIdx] = updatedTarget;
        
        // Update currentTarget if it's the same one
        if (state.currentTarget.data?.id === updatedTarget.id) {
          state.currentTarget.data = updatedTarget;
        }
      })
      .addCase(updateTargetThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete target
      .addCase(deleteTargetThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTargetThunk.fulfilled, (state, action) => {
        state.loading = false;
        const targetId = action.payload;
        
        // Remove from targets list
        state.targets = state.targets.filter(t => t.id !== targetId);
        state.count -= 1;
        
        // Remove from myTargets list
        state.myTargets.list = state.myTargets.list.filter(t => t.id !== targetId);
        
        // Clear currentTarget if it's the deleted one
        if (state.currentTarget.data?.id === targetId) {
          state.currentTarget.data = null;
        }
      })
      .addCase(deleteTargetThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch target summary
      .addCase(fetchTargetSummaryThunk.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(fetchTargetSummaryThunk.fulfilled, (state, action) => {
        state.summary.loading = false;
        state.summary.totalActiveTargets = action.payload.total_active_targets;
        state.summary.notStarted = action.payload.not_started;
        state.summary.inProgress = action.payload.in_progress;
        state.summary.completed = action.payload.completed;
        state.summary.employeesWithoutTargets = action.payload.employees_without_targets;
      })
      .addCase(fetchTargetSummaryThunk.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload;
      });
  },
});

export const {
  resetTargets,
  resetMyTargets,
  resetCurrentTarget,
  resetTargetProgress,
  resetTargetSummary
} = employeeTargetSlice.actions;

export default employeeTargetSlice.reducer;